import { Handle } from '@xyflow/react';
import { getHandlePositions, getNodeFlowId } from './nodeLayout';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { isReadOnly } from 'js/lib/ACLUtils';
import useResourceEditProvider from 'js/components/ResourceEditProvider/useResourceEditProvider';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import Typography from 'new/elements/Typography';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import Menu from 'js/components/elements/menu/Menu';
import { NodeBox } from 'js/components/pipelines/cards/components/NodeBox';
import { ReactComponent as CodeIcon } from 'new/components/icons/CodeContainerIcon.svg';
import { useByoCodeConfigured } from './useByoCodeConfigured';

import { ReactComponent as EditFileIcon } from 'new/components/icons/editFile.svg';
import classes from '../FlowNodes.module.scss';
import nameClasses from 'js/components/pipelines/cards/components/ResourceName/ResourceName.module.scss';
import { HANDLE_PIN_TO_CARD_CENTER } from '../consts';

/**
 * CodeNode — the BYO (`flow_type: 'custom'`) flow's stand-in for the nexset
 * node.
 *
 * BYO flows have no detected nexset: the data goes Source → **Code** →
 * Destination. This node renders inline between the source and the sink (the
 * mapper injects it for custom-flow sources; {@link createEdges} reroutes the
 * source→sink edge through it). It mirrors the nexset node on the canvas: it
 * reuses the EXACT dark resource-name chip ({@link ResourceName}'s
 * `.nameContainer`) used by SOURCE/NEXSET/DESTINATION (label "CODE" + info +
 * a ⋯ menu), plus the same blue "+" `ActionMenu` whose **Send to Destination**
 * item opens the create-sink modal exactly like the nexset's.
 */
function CodeNode(props: any) {
  const { data } = props;
  const { item, layoutDirection = 'LR', isPreview = false, isCreateFlow = false } = data;
  const { setAction } = useResourceEditProvider();
  const { refreshPipelineById } = usePipelinesAdapter();

  const sourceId = item?.id;
  // /flows never returns code_container_id on data_sources (useByoCodeConfigured's
  // TODO), so the real id is often unavailable client-side. `ui.byo_code_predefined_id`
  // is a display-only fallback (only set in "Use Existing Function" mode) — kept out of
  // useByoCodeConfigured's own codeContainerId/hasRealSignal so it can't flip
  // codeConfigured early off a draft save (see useByoSourceSave.ts:157).
  const predefinedContainerId = item?.source_config?.['ui.byo_code_predefined_id'] ?? null;
  const flowId = getNodeFlowId(data);
  const nodeKey = item?.nodeKey ?? item?.flow_node_id ?? item?.origin_node_id;

  const { source: sourcePosition, target: targetPosition } = getHandlePositions(layoutDirection);

  // Virtual shell item (BYO create flow, pre-source) — clicks are no-ops.
  const isByoShell = !!item?.isByoSourceShell;
  // View-only (collaborator) users can view the code but never edit it —
  // same ACL helper the other canvas cards gate on.
  const readOnly = isReadOnly(item ?? {});
  // Step 2 is only "Blocked" before the source exists; once it does, writing
  // code IS the next step, so the shell is live (unless preview/view-only).
  const canEdit = !isByoShell && !isPreview && !readOnly;

  const onEditCode = () => {
    if (isByoShell || readOnly) return;
    setAction?.({
      type: 'edit',
      resource: RESOURCE_TYPE_SOURCE,
      id: sourceId,
      flowType: FLOW_TYPE_CUSTOM as any,
      queryParams: { byoSection: 'code' } as any,
      onComplete: (result: any) => {
        if (result != null) refreshPipelineById(flowId);
      },
    });
  };

  // "View code" without entering the edit form — pending-issues #7. Opens
  // the same modal with `viewOnly` so ByoEditCodeModal renders a read-only
  // Monaco panel (no Save, no Packages/Variables/Test panel) instead of the
  // full edit UI. No onComplete refresh needed — a pure view can't change
  // anything for the canvas to refresh.
  const onViewCode = () => {
    if (isByoShell) return;
    setAction?.({
      type: 'edit',
      resource: RESOURCE_TYPE_SOURCE,
      id: sourceId,
      flowType: FLOW_TYPE_CUSTOM as any,
      queryParams: { byoSection: 'code', viewOnly: true } as any,
    });
  };

  // codeConfigured — the real signal (code container attached, or the
  // ui.byo_code_configured flag). checking — flow-listing/detail views only:
  // the backfill fetch for the real signal is still in flight. Create mode
  // (isCreateFlow) never checks and never assumes — see the hook's doc.
  const { codeConfigured, checking, codeContainerId } = useByoCodeConfigured({
    item,
    isByoShell,
    isPreview,
    isCreateFlow,
  });

  const menuItems = [
    // View is only useful once real code exists to look at — an
    // unconfigured shell has nothing to show and always routes into
    // onEditCode's isByoShell no-op anyway.
    ...(codeConfigured
      ? [{ label: 'View code', icon: <VisibilityOutlinedIcon />, onClick: onViewCode }]
      : []),
    ...(readOnly ? [] : [{ label: 'Edit code', icon: <EditFileIcon />, onClick: onEditCode }]),
  ];

  // The container id to show/copy — never the SOURCE id. The predefined
  // (reusable function) id is the attached container in that mode.
  const displayContainerId = codeContainerId ?? predefinedContainerId;
  // The CODE node's own name: the container's, when it's loaded — never the
  // source's (that put two resources under one name on the canvas).
  const containerName = useSelector((state: any) =>
    displayContainerId != null ? state.entities?.transforms?.[displayContainerId]?.name : null,
  );

  if (!item) return <div />;

  return (
    <div style={{ whiteSpace: 'nowrap' }} data-testid="byo-canvas-code-node">
      <div style={{ position: 'relative', width: 232 }}>
        {codeConfigured ? (
          <>
            {/* Reuse the EXACT resource-name chip used by SOURCE/NEXSET/DESTINATION. */}
            <div className={classNames(nameClasses.nameContainer, nameClasses.visible)}>
              <Typography className={nameClasses.resourceType} variant="overline">
                CODE
              </Typography>
              <div className={nameClasses.resourceNameActions}>
                <Menu
                  items={menuItems}
                  horizontalDots
                  inPipeline
                  menuButtonClassName={nameClasses.resourceActionButton}
                />
              </div>
            </div>

            <NodeBox data={item} size="small" hideInfo data-testid="code-node-box">
              <div className={classes.codeNodeBody}>
                <div className={classes.codeNodeHead}>
                  <CodeIcon className={classes.codeNodeIcon} />
                  <span className={classes.codeNodeTitle}>{containerName || 'Code Processor'}</span>
                </div>
                {displayContainerId != null && (
                  <ChipWithCopy
                    toCopy={String(displayContainerId)}
                    tooltipTitle="Copy code container ID"
                    tooltipCopiedTitle="Copied!"
                    data-testid="code-node-container-id"
                  >
                    ID {displayContainerId}
                  </ChipWithCopy>
                )}
              </div>
            </NodeBox>
          </>
        ) : (
          <>
            <button
              type="button"
              className={classes.codeShell}
              onClick={canEdit ? onEditCode : undefined}
              aria-disabled={!canEdit}
              style={canEdit ? undefined : { cursor: 'default' }}
              data-testid="code-node-placeholder"
            >
              <span className={classes.shellRow}>
                <LockOutlinedIcon className={classes.shellIcon} />
                <span className={classes.shellTitle}>Code Processor</span>
              </span>
            </button>
          </>
        )}
      </div>

      {/* Badge + caption below the code shell — outside the relative div so they
          don't inflate its height and shift the edge handles. */}
      {!codeConfigured && !isPreview && (
        <div className={classes.shellFooter}>
          {checking ? (
            <span className={classNames(classes.shellBadge, classes.shellBadgeChecking)}>
              <CircularProgress size={10} className={classes.shellBadgeSpinner} />
              Checking...
            </span>
          ) : (
            <span className={classes.shellBadge}>{isByoShell ? 'Blocked' : 'To do'}</span>
          )}
          <span className={classes.shellCaption}>Step 2: Write your code</span>
        </div>
      )}

      {/* Incoming from the source. Pin to shell-card center when unconfigured so
          the edge doesn't arrive at 50% of (shell + badge/caption footer). */}
      <Handle
        isConnectable={false}
        className={classes.handle_left}
        type="target"
        position={targetPosition}
        id="left"
        style={!codeConfigured ? { top: HANDLE_PIN_TO_CARD_CENTER } : undefined}
      />
      {/* Outgoing to the destination (when one exists). Pin when unconfigured
          so the edge leaves from card center, not 50% of (card + footer). */}
      <Handle
        isConnectable={false}
        className={classes.handle}
        type="source"
        position={sourcePosition}
        id="right"
        style={!codeConfigured ? { top: HANDLE_PIN_TO_CARD_CENTER } : undefined}
      />
    </div>
  );
}

export default memo(CodeNode, (prev, next) => {
  const keys = ['item', 'layoutDirection', 'parentId', 'flowId', 'isPreview', 'isCreateFlow'];
  return !keys.some((key) => prev?.data?.[key] !== next?.data?.[key]);
});
